import { useCallback, useEffect, useRef, useState } from 'react';

// Same fetch-into-state approach as the TripThrottling page (no react-query
// cache: every visit shows the live state). `load` is re-run whenever `key`
// changes; a stale response from an earlier key is dropped.
const useSdpFetch = (load, key) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const latest = useRef(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  const refetch = useCallback(async () => {
    const id = ++latest.current;
    setLoading(true);
    try {
      const result = await loadRef.current();
      if (id === latest.current) {
        setData(result);
        setError(null);
      }
    } catch (e) {
      if (id === latest.current) setError(e.message || 'Request failed');
    } finally {
      if (id === latest.current) setLoading(false);
    }
  }, []);

  // A new key (new filters, another readout window) must not keep showing
  // the previous key's rows, even if its own request fails; a refetch of the
  // same key keeps them while it loads.
  useEffect(() => {
    setData(null);
    setError(null);
    refetch();
  }, [refetch, key]);

  return { data, loading, error, refetch };
};

export default useSdpFetch;
